import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../../config/api";

const SHEET_OPTIONS = [
  { key: "staff", label: "Штатное расписание" },
  { key: "tmc", label: "ТМЦ" },
  { key: "amortization", label: "Амортизация" },
  { key: "extraCosts", label: "Доп.затраты" },
  { key: "summary", label: "Итоговая страница" },
];

function formatPeriod(month, year) {
  if (!month || !year) return "—";
  return `${String(month).padStart(2, "0")}.${year}`;
}

function Badge({ type }) {
  if (!type) return null;
  const map = {
    signed: { text: "Подписана", color: "#fff7ed", border: "#fdba74", textColor: "#c2410c" },
    actual: { text: "Актуальна", color: "#e0f2fe", border: "#38bdf8", textColor: "#0369a1" },
    source: { text: "Текущая", color: "#ede9fe", border: "#c4b5fd", textColor: "#5b21b6" },
  };
  const meta = map[type];
  if (!meta) return null;
  return (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: 999,
        border: `1px solid ${meta.border}`,
        background: meta.color,
        color: meta.textColor,
        fontSize: 12,
        fontWeight: 600,
        marginLeft: 6,
      }}
    >
      {meta.text}
    </span>
  );
}

export default function GroupPushModal({ open, defaultSheetKeys = [], onClose, cardCode, cardTitle }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [cards, setCards] = useState([]);
  const [groupId, setGroupId] = useState(null);
  const [selectedSheets, setSelectedSheets] = useState(() => new Set(defaultSheetKeys));
  const [selectedTargets, setSelectedTargets] = useState(() => new Set());

  useEffect(() => {
    if (!open) return;
    setSelectedSheets(new Set(defaultSheetKeys));
  }, [open, defaultSheetKeys]);

  useEffect(() => {
    if (!open || !cardCode) return;
    setLoading(true);
    setError("");
    fetch(apiUrl(`/api/card-calc/${encodeURIComponent(cardCode)}/group-targets`))
      .then((r) => r.json())
      .then((data) => {
        setGroupId(data?.groupId || null);
        const list = Array.isArray(data?.cards) ? data.cards : [];
        setCards(list);
        const defaultTargets = list.filter((c) => !c.isSource && !c.lockedReason).map((c) => Number(c.objectCode));
        setSelectedTargets(new Set(defaultTargets));
        setLoading(false);
      })
      .catch(() => {
        setError("Не удалось загрузить состав группы");
        setLoading(false);
      });
  }, [open, cardCode]);

  const eligibleSheets = useMemo(() => SHEET_OPTIONS, []);
  const selectableCards = useMemo(() => cards.filter((c) => !c.isSource), [cards]);

  const toggleSheet = (key) => {
    setSelectedSheets((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleCard = (code) => {
    setSelectedTargets((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!cardCode) return;
    if (!selectedSheets.size) {
      alert("Выберите хотя бы один лист для применения");
      return;
    }
    if (!selectedTargets.size) {
      alert("Нужно выбрать хотя бы одну карточку группы");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const resp = await fetch(apiUrl(`/api/card-calc/${encodeURIComponent(cardCode)}/group-push`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sheets: Array.from(selectedSheets),
          targets: Array.from(selectedTargets),
        }),
      });
      const data = await resp.json().catch(() => null);
      if (!resp.ok) {
        throw new Error(data?.error || `HTTP ${resp.status}`);
      }
      const skipped = data?.skipped || {};
      let message = "Изменения применены";
      const notes = [];
      if (skipped.foreign?.length) notes.push(`Не в группе: ${skipped.foreign.join(", ")}`);
      if (skipped.missing?.length) notes.push(`Не найдены: ${skipped.missing.join(", ")}`);
      if (skipped.signed?.length) notes.push(`Подписаны: ${skipped.signed.join(", ")}`);
      if (skipped.actual?.length) notes.push(`Актуальны: ${skipped.actual.join(", ")}`);
      if (notes.length) message += `\n${notes.join("\n")}`;
      alert(message);
      onClose?.();
    } catch (e) {
      setError(e?.message || "Ошибка применения изменений");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(13,16,23,0.54)",
        backdropFilter: "blur(2px)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "min(960px, 100%)",
          maxHeight: "90vh",
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 30px 80px rgba(15,23,42,0.3)",
          padding: 24,
          overflow: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>Применить изменения в группе</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ color: "#475569", marginBottom: 16 }}>
          Источник: <strong>{cardTitle || cardCode || "—"}</strong>
          {groupId ? ` · Группа #${groupId}` : ""}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 20 }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Какие листы копируем?</div>
            {eligibleSheets.map((sheet) => (
              <label key={sheet.key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={selectedSheets.has(sheet.key)}
                  onChange={() => toggleSheet(sheet.key)}
                />
                {sheet.label}
              </label>
            ))}
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>Куда применяем</div>
            {loading ? (
              <div>Загрузка…</div>
            ) : error ? (
              <div style={{ color: "#b91c1c" }}>{error}</div>
            ) : selectableCards.length === 0 ? (
              <div style={{ color: "#6b7280" }}>В группе нет других карточек</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {selectableCards.map((card) => {
                  const lockedReason = card.lockedReason;
                  const disabled = !!lockedReason;
                  const code = Number(card.objectCode);
                  return (
                    <label
                      key={card.id || card.objectCode}
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 10,
                        padding: "10px 12px",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        opacity: disabled ? 0.5 : 1,
                        background: disabled ? "#f8fafc" : "#fff",
                      }}
                    >
                      <input
                        type="checkbox"
                        disabled={disabled}
                        checked={selectedTargets.has(code)}
                        onChange={() => toggleCard(code)}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{card.objectCodeFull || card.objectCode}</div>
                        <div style={{ fontSize: 13, color: "#475569" }}>
                          {card.objectName || "—"}
                          <span style={{ marginLeft: 12, color: "#94a3b8" }}>
                            {formatPeriod(card.periodMonth, card.periodYear)}
                          </span>
                        </div>
                      </div>
                      {lockedReason && <Badge type={lockedReason} />}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20, gap: 12 }}>
          <div style={{ fontSize: 13, color: "#475569" }}>
            Изменения будут применены только к карточкам без подписи и актуализации.
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={onClose} style={{ padding: "10px 18px", borderRadius: 10, border: "1px solid #cbd5f5", background: "#fff" }}>
              Отмена
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                padding: "10px 22px",
                borderRadius: 10,
                border: "none",
                background: "linear-gradient(120deg, #4c1d95, #7c3aed)",
                color: "#fff",
                fontWeight: 600,
                boxShadow: "0 12px 30px rgba(76,29,149,0.25)",
                cursor: submitting ? "wait" : "pointer",
              }}
            >
              {submitting ? "Применение…" : "Применить"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
